/**
 * What people see of an announcement: the strip under the header, and the
 * centred pop-up over a blurred, dimmed page.
 *
 * Designed in the announcements sketch and chosen from it: the banner is the
 * full-width strip (option A), and the pop-up has three styles an administrator
 * picks per announcement -
 *
 * * **classic** - an icon, the message, one button;
 * * **schedule** - the event's From → Until band, a live countdown, the message;
 * * **release notes** - "What's new": an illustrated band, the version, and what
 *   changed one line at a time. Readable again later from the account menu,
 *   where several are stepped through with the pager.
 *
 * A **critical** pop-up closes only with its button - not Esc, not the scrim -
 * because it is the one message people must not wave away by reflex.
 *
 * `renderPopupCard` is shared with Administration's live preview, so what an
 * administrator sees while writing is the card people will get.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  closeOpenedAnnouncement,
  dismissBanner,
  liveBanners,
  markPopupSeen,
  openAnnouncement,
  openedAnnouncement,
  pendingPopups,
  startAnnouncementPolling,
} from '../../state/announcements.ts';
import { newBuildAvailable } from '../../state/app-build.ts';
import type { Announcement, AnnouncementInput, AnnouncementTone } from '../../api/types.ts';
import { eventClock, eventWindow, type EventClock, TONE_LABEL, utcDay, utcTime } from './format.ts';

/* ── glyphs ───────────────────────────────────────────────── */

export function toneIcon(tone: AnnouncementTone, size = 18): TemplateResult {
  if (tone === 'critical') {
    return html`<svg width=${size} height=${size} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/></svg>`;
  }
  if (tone === 'maintenance') {
    return html`<svg width=${size} height=${size} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.4-.6-.6-2.4z"/></svg>`;
  }
  return html`<svg width=${size} height=${size} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/></svg>`;
}

const closeIcon = html`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>`;
const tickIcon = html`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>`;

/* ── the pop-up card ──────────────────────────────────────── */

export interface PopupCardOptions {
  onClose: () => void;
  /** The pager, when several are stepped through (release notes). */
  pager?: { index: number; count: number; go: (index: number) => void } | undefined;
  /** Administration's inline preview: no dialog semantics. */
  inline?: boolean;
  /**
   * Release notes on a tab still running the previous build: the primary
   * button becomes Reload, and Close stays beside it.
   */
  onReload?: (() => void) | undefined;
}

type CardData = AnnouncementInput & { id?: string };

function releaseBand(version: string | null): TemplateResult {
  return html`<div class="ann-release-band" aria-hidden="true">
    <svg viewBox="0 0 520 120" preserveAspectRatio="xMidYMid slice">
      <g fill="none" stroke="currentColor" stroke-linecap="round">
        <path d="M-10 92 C 60 70, 110 100, 180 80 S 300 60, 360 78 S 470 96, 530 70" stroke-width="2" opacity=".85" />
        <path d="M-10 104 C 70 86, 120 112, 190 94 S 310 76, 370 92 S 480 108, 530 86" stroke-width="1.2" opacity=".45" />
        <path d="M-10 60 C 50 50, 120 66, 200 52 S 330 36, 400 50 S 480 62, 530 44" stroke-width="1" opacity=".3" stroke-dasharray="3 5" />
      </g>
      <circle cx="360" cy="78" r="4.5" fill="currentColor" />
    </svg>
    ${version ? html`<span class="ann-release-version">${version}</span>` : nothing}
  </div>`;
}

function clockText(clock: EventClock): TemplateResult {
  switch (clock.phase) {
    case 'starts': return html`Starts in <b data-ann-countdown .textContent=${clock.left}></b>`;
    case 'remaining': return html`Time remaining <b data-ann-countdown .textContent=${clock.left}></b>`;
    case 'under way': return html`<b>Under way now</b>`;
    case 'over': return html`<b>Finished</b>`;
  }
}

export function renderPopupCard(a: CardData, opts: PopupCardOptions): TemplateResult {
  const critical = a.tone === 'critical';
  const style = a.popup_style === 'none' ? 'classic' : a.popup_style;
  const role = opts.inline ? nothing : 'dialog';
  const primaryLabel = style === 'release_notes' ? 'Close' : critical ? 'I understand' : 'Got it';
  const reload = style === 'release_notes' ? opts.onReload : undefined;
  const pager = opts.pager && opts.pager.count > 1 ? opts.pager : null;

  const foot = html`<div class="ann-modal-foot">
    ${pager
      ? html`<span class="ann-pager">
          <button type="button" class="ann-pager-btn" aria-label="Newer"
            ?disabled=${pager.index === 0} @click=${() => pager.go(pager.index - 1)}>‹</button>
          <span>${pager.index + 1} of ${pager.count}</span>
          <button type="button" class="ann-pager-btn" aria-label="Older"
            ?disabled=${pager.index >= pager.count - 1} @click=${() => pager.go(pager.index + 1)}>›</button>
        </span>`
      : nothing}
    <span class="ann-spacer"></span>
    ${reload
      ? html`<button type="button" class="btn btn-secondary" @click=${opts.onClose}>Close</button>
          <button type="button" class="btn btn-primary ann-primary" @click=${reload}>Reload to update</button>`
      : html`<button type="button" class="btn btn-primary ann-primary" @click=${opts.onClose}>${primaryLabel}</button>`}
  </div>`;

  const badge = html`<span class="ann-badge">${toneIcon(a.tone, 13)}${
    style === 'release_notes' ? "What's new" : TONE_LABEL[a.tone]}</span>`;
  const body = a.body ? html`<p class="ann-message">${a.body}</p>` : nothing;

  if (style === 'schedule') {
    const clock = eventClock(a);
    return html`<div class="ann-modal ann-schedule tone-${a.tone}" role=${role} aria-modal=${opts.inline ? nothing : 'true'} aria-labelledby="ann-title">
      <div class="ann-when-band">
        <div class="ann-slot"><div class="ann-slot-k">From</div><div class="ann-slot-h">${utcTime(a.event_starts_at) || '--:--'}</div><div class="ann-slot-d">${utcDay(a.event_starts_at)}</div></div>
        <svg class="ann-arrow" width="28" height="12" viewBox="0 0 28 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M1 6h25M21 1l5 5-5 5"/></svg>
        <div class="ann-slot"><div class="ann-slot-k">Until</div><div class="ann-slot-h">${utcTime(a.event_ends_at) || '--:--'}</div><div class="ann-slot-d">${a.event_ends_at ? `${utcDay(a.event_ends_at)} · UTC` : 'UTC'}</div></div>
      </div>
      <div class="ann-modal-body">
        ${badge}
        <h2 class="ann-title" id="ann-title">${a.title}</h2>
        ${body}
        ${clock ? html`<div class="ann-countdown" data-ann-phase=${clock.phase}>${clockText(clock)}</div>` : nothing}
      </div>
      ${foot}
    </div>`;
  }

  if (style === 'release_notes') {
    return html`<div class="ann-modal ann-release tone-${a.tone}" role=${role} aria-modal=${opts.inline ? nothing : 'true'} aria-labelledby="ann-title">
      ${releaseBand(a.version_label)}
      <div class="ann-modal-body">
        ${badge}
        <h2 class="ann-title" id="ann-title">${a.title}</h2>
        ${body}
        ${a.highlights.length
          ? html`<ul class="ann-highlights">${a.highlights.map((line) => html`<li>${tickIcon}<span>${line}</span></li>`)}</ul>`
          : nothing}
      </div>
      ${foot}
    </div>`;
  }

  return html`<div class="ann-modal ann-classic tone-${a.tone}" role=${role} aria-modal=${opts.inline ? nothing : 'true'} aria-labelledby="ann-title">
    <div class="ann-modal-body">
      <div class="ann-icon-disc">${toneIcon(a.tone, 20)}</div>
      <h2 class="ann-title" id="ann-title">${a.title}</h2>
      ${body}
      ${a.event_starts_at ? html`<div class="ann-countdown">${eventWindow(a)}</div>` : nothing}
    </div>
    ${foot}
  </div>`;
}

/* ── the strip under the header ───────────────────────────── */

@customElement('announcement-strip')
export class AnnouncementStrip extends LightElement {
  private store = new StoreController(this, [liveBanners]);
  @state() private index = 0;

  override connectedCallback(): void {
    super.connectedCallback();
    startAnnouncementPolling();
  }

  override render(): TemplateResult | typeof nothing {
    const banners = liveBanners.value;
    if (!banners.length) return nothing;
    const index = Math.min(this.index, banners.length - 1);
    const a = banners[index]!;
    const when = a.event_starts_at ? eventWindow(a) : '';
    const hasMore = !!a.body || a.popup_style !== 'none' || a.highlights.length > 0;
    return html`<div class="ann-strip tone-${a.tone}" role=${a.tone === 'critical' ? 'alert' : 'status'}>
      <span class="ann-strip-icon">${toneIcon(a.tone, 16)}</span>
      <span class="ann-strip-title">${a.title}</span>
      ${a.body ? html`<span class="ann-strip-body">${a.body}</span>` : nothing}
      <span class="ann-strip-end">
        ${when ? html`<span class="ann-strip-when">${when}</span>` : nothing}
        ${hasMore
          ? html`<button type="button" class="ann-strip-link" @click=${() => openAnnouncement(a)}>Details</button>`
          : nothing}
        ${banners.length > 1
          ? html`<span class="ann-strip-pager">
              <button type="button" aria-label="Previous announcement" @click=${() => { this.index = (index - 1 + banners.length) % banners.length; }}>‹</button>
              ${index + 1}/${banners.length}
              <button type="button" aria-label="Next announcement" @click=${() => { this.index = (index + 1) % banners.length; }}>›</button>
            </span>`
          : nothing}
        ${a.dismissible
          ? html`<button type="button" class="ann-strip-close" aria-label="Dismiss this announcement"
              title="Dismiss" @click=${() => dismissBanner(a.id)}>${closeIcon}</button>`
          : nothing}
      </span>
    </div>`;
  }
}

/* ── the pop-up host ──────────────────────────────────────── */

@customElement('announcement-popup-host')
export class AnnouncementPopupHost extends LightElement {
  private store = new StoreController(this, [pendingPopups, openedAnnouncement, newBuildAvailable]);
  /**
   * Whether announcements arriving on their own are shown here. Off in
   * Administration: the people writing announcements should not have one land
   * on the form they are writing it in. Opened ones - Preview - still show.
   */
  @property({ type: Boolean }) arrivals = true;
  private ticker: number | undefined;
  private shownId: string | null = null;
  private returnFocus: HTMLElement | null = null;

  private onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    const a = this.current();
    if (a && a.tone !== 'critical') {
      e.preventDefault();
      this.close();
    }
  };

  override connectedCallback(): void {
    super.connectedCallback();
    if (this.arrivals) startAnnouncementPolling();
    document.addEventListener('keydown', this.onKey);
    // The countdown ticks in place rather than re-rendering the card each
    // second. When the phase changes (it starts, or it ends) the card re-renders.
    this.ticker = window.setInterval(() => {
      const box = this.querySelector<HTMLElement>('[data-ann-phase]');
      const a = this.current();
      if (!box || !a) return;
      const clock = eventClock(a);
      if (!clock || clock.phase !== box.dataset['annPhase']) { this.requestUpdate(); return; }
      const el = box.querySelector<HTMLElement>('[data-ann-countdown]');
      if (el) el.textContent = clock.left;
    }, 1000);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.onKey);
    window.clearInterval(this.ticker);
  }

  /** An opened one wins over an arriving one: somebody asked for it. */
  private current(): Announcement | null {
    const opened = openedAnnouncement.value;
    if (opened) return opened.list[opened.index] ?? null;
    return this.arrivals ? pendingPopups.value[0] ?? null : null;
  }

  private close(): void {
    const opened = openedAnnouncement.value;
    if (opened) closeOpenedAnnouncement();
    else {
      const a = pendingPopups.value[0];
      if (a) markPopupSeen(a);
    }
  }

  /** Close the release notes, then load the build they describe. */
  private async closeAndReload(): Promise<void> {
    const opened = openedAnnouncement.value;
    const a = this.current();
    if (opened) closeOpenedAnnouncement();
    else if (a) {
      // Wait (briefly) for the show-once receipt, or it pops up again.
      await Promise.race([markPopupSeen(a), new Promise((r) => setTimeout(r, 2000))]);
    }
    window.location.reload();
  }

  override updated(): void {
    const a = this.current();
    const id = a ? `${a.id}:${openedAnnouncement.value?.index ?? ''}` : null;
    if (id === this.shownId) return;
    if (id && !this.shownId) this.returnFocus = document.activeElement as HTMLElement | null;
    this.shownId = id;
    if (id) {
      this.querySelector<HTMLElement>('.ann-primary')?.focus({ preventScroll: true });
    } else {
      this.returnFocus?.focus?.({ preventScroll: true });
      this.returnFocus = null;
    }
  }

  override render(): TemplateResult | typeof nothing {
    const a = this.current();
    if (!a) return nothing;
    const opened = openedAnnouncement.value;
    const pager = opened && opened.list.length > 1
      ? { index: opened.index, count: opened.list.length,
          go: (index: number) => openedAnnouncement.set({ ...opened, index }) }
      : undefined;
    return html`<div class="ann-scrim"
      @click=${(e: MouseEvent) => { if (e.target === e.currentTarget && a.tone !== 'critical') this.close(); }}>
      ${renderPopupCard(a, {
        onClose: () => this.close(),
        pager,
        onReload: newBuildAvailable.value ? () => void this.closeAndReload() : undefined,
      })}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'announcement-strip': AnnouncementStrip;
    'announcement-popup-host': AnnouncementPopupHost;
  }
}
